<template>
  <div class="event-info-container">
    <div class="event-info-title title" v-move>
      <div class="event-info-title-content">
        <div class="ellipsis" v-if="!loadingFlag">
          {{ target['pushName'] }}
        </div>
        <div v-if="!loadingFlag">
          {{ detailTarget['eventTime'] }}
        </div>
        <svg-icon iconName="loading"
          v-if="loadingFlag"
          :style="{
            height: '1.875rem',
            width: '1.875rem',
          }"
        ></svg-icon>
      </div>
      <div class="event-info-title-close">
        <span @click="close">
          <svg-icon iconName="circle-close2"></svg-icon>
        </span>
      </div>
    </div>
    <div class="event-info-body" v-if="!loadingFlag">
      <span class="attr-span">
        情报描述：
      </span>
      <div class="event-info-body-desc scroll-y">
        {{ detailTarget['eventDetail'] }}
      </div>
      <!-- 图片 -->
      <el-image
        v-if="detailTarget['imagePath'] && isImage(detailTarget['imagePath'])"
        :src="detailTarget['imagePath']"
        :preview-src-list="[detailTarget['imagePath']]"
        :preview-teleported="true"
        fit="contain"
      >
        <template #placeholder>
          <img :src="detailTarget['imagePath']" />
        </template>
      </el-image>
      <!-- 视频 -->
      <video
        v-else-if="detailTarget['imagePath'] && isVideo(detailTarget['imagePath'])"
        controls
        preload="metadata"
        class="event-info-media-video"
        :src="detailTarget['imagePath']"
      >
        您的浏览器不支持HTML5视频
      </video>
      <!-- 音频（imagePath 为音频文件） -->
      <div
        v-else-if="detailTarget['imagePath'] && isAudio(detailTarget['imagePath'])"
        class="event-info-media-audio"
      >
        <AudioPlayer :audioUrl="detailTarget['imagePath']"></AudioPlayer>
      </div>
      <!-- 通话音频（audioUrl 字段，独立于 imagePath） -->
      <div
        v-if="detailTarget['audioUrl']"
        class="event-info-media-audio"
      >
        <AudioPlayer :audioUrl="detailTarget['audioUrl']"></AudioPlayer>
      </div>
      <!-- 轨迹地图 -->
      <LeafletConstructor 
        v-if="!detailTarget['imagePath'] && detailTarget['track'] && detailTarget['track']['trackArr']"
        @mapInit="mapInit"
        :isAddBaseLayer="true"
        height="22.5rem"
      />
    </div>
  </div>
</template>

<script setup>
import { 
  onBeforeMount, onMounted, onUnmounted, onUpdated,
  computed, watch, ref, nextTick
} from 'vue';
import store from '@/store';
import { isNull, dayjs, message } from '@/utils/js/initVariable';

import { getEventAllDetail } from '@/api/UserDrawApi';
import LeafletConstructor from '@/components/LeafletConstructor';
import SvgIcon from '@/components/SvgIcon';
import { isImage, isAudio, isVideo } from '@/utils/js/file';
import AudioPlayer from '@/components/AudioPlayer';
import { homeOtherComUseLayerGroupCreate } from '@/utils/js/leafletUtils';

import { antPath } from '@/utils/js/leaflet-plug/leaflet-ant-path-master/src/plugin/main.js'

// props
const props = defineProps({
  target: { type: Object, },
});
// emit
const emit = defineEmits([
  'close'
]);

const dataImageRef = ref();

const dataTarget = computed(()=> props.target );

watch(dataTarget, (newd, old)=>{
  detailTarget.value = {};
  getEventDetailFn(newd);
})

const mapInit = (map)=>{
  // console.log(map, detailTarget.value);
  const otherComUseLayerGroup = homeOtherComUseLayerGroupCreate();
  map.addLayer(otherComUseLayerGroup);
  
  const track = detailTarget.value['track'],
        trackList = track && track['trackArr'];
  if(!trackList)return;
  const path = L.polyline.antPath(
    trackList
  , {
    color: '#FF0000',
    pulseColor: '#FFFFFF',
    delay: 400,
    weight: 3,
    dashArray: [10, 20]
  }).addTo(map);
  path.getBounds().isValid() && map.fitBounds(path.getBounds());
}

const close = ()=>{
  emit('close');
}

const detailTarget = ref({}), loadingFlag = ref(true),
getEventDetailFn = (target)=>{
  // console.log(target);
  loadingFlag.value = true;
  getEventAllDetail(target).then(res=>{
    if(res.data.code == 1004){
      detailTarget.value = JSON.parse(res.data.data);
    }
    loadingFlag.value = false;
  }).catch(err=>{
    loadingFlag.value = false;
    console.log(err);
  });
}

// 组件挂载前
onBeforeMount(()=>{

});
// 组件挂载后
onMounted(()=>{
  getEventDetailFn(props.target);
});
// 组件更新后
onUpdated(()=>{

});
// 组件销毁后
onUnmounted(()=>{

});

</script>

<style lang='less'>

</style>

<style lang='less' scoped>
.event-info-container{
  position: absolute;
  right: 60px;
  top: 60px;
  border-radius: var(--borderRadiusSize);
  background: var(--backgroundColor_18);
  border: 2px solid var(--borderColor_8);
  box-shadow: 1px 2px 5px  var(--boxShadowColor_2);  
  height: max-content;
  width: 400px;
  backdrop-filter: blur(15px);
  padding: 10px;
  box-sizing: content-box;
  .event-info-body{
    .attr-span{
      line-height: 23px;
      font-size: 14px;
      color: var(--fontColor_13);
      font-family: 'OPPOSans-M';
    }
    .event-info-body-desc{
      padding: 5px;
      font-size: 14px;
      font-weight: 300px;
      color: var(--fontColor_13);
      line-height: 26px;
      max-height: 200px;
      font-family: 'OPPOSans-L';
    }
    :deep(.el-image){
      width: 100%;
      height: auto;
      img {
        width: 100%;
        max-height: 300px;
      }
      .el-image__wrapper{
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--fontColor_12);
        background-color: var(--backgroundColor_24);
        font-size: 16px;
        img{
          width: 100%;
          height: 100%;
        }
      }
    }
    .event-info-media-video {
      width: 100%;
      max-height: 360px;
      border-radius: 4px;
      margin-top: 8px;
    }
    .event-info-media-audio {
      width: 100%;
      margin-top: 8px;
    }
  }
  .event-info-title{
    display: flex;
    .event-info-title-content{
      flex: 1;
      min-width: 0;
      div{
        &:nth-child(1){
          font-size: 17px;
          line-height: 26px;
          font-family: 'OPPOSans-B';
          color: var(--fontColor_13);
        }
        &:nth-child(2){
          font-size: 14px;
          line-height: 26px;
          font-family: 'OPPOSans-B';
          color: var(--fontColor_12);
        }
      }
    }
    .event-info-title-close{
      .svg-icon{
        width: 28px;
        height: 28px;
        fill: var(--iconColor_6);
        cursor: pointer;
        &:hover{
          fill: var(--iconColor_5);
        }
      }
    }
  }
}
</style>